// Role hook for the AdminLayout sidebar. Sources the role from the
// authenticated user's staffRole (returned by /auth/me, see
// apps/api/src/modules/auth/routes.ts), with a super-admin-only
// "preview as another role" override stored in localStorage.
//
// Non-super-admin users always render with their actual staff_role —
// the switcher is hidden in AdminLayout for them. The API enforces
// permissions independently via requireSection; this hook only
// controls UI visibility.

import { useEffect, useState } from 'react';
import { useAuth } from './auth';
import type { RoleId } from '@/data/mock';

const STORAGE_KEY = 'dc-bo-role';
const FALLBACK_ROLE: RoleId = 'super_admin';

const VALID_ROLES = new Set<RoleId>([
  'super_admin', 'operations', 'finance', 'marketing', 'support', 'viewer',
]);

function readPreview(): RoleId | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw && VALID_ROLES.has(raw as RoleId)) return raw as RoleId;
  return null;
}

export function useRole(): { role: RoleId; setRole: (r: RoleId) => void; canSwitch: boolean } {
  const { user } = useAuth();
  // Actual role from the server. Falls back to super_admin only when no
  // staffRole is present (legacy admin row from before 0093 backfill, or
  // anonymous render path) — the sidebar will be too permissive in that
  // case but the API will still 403 unsupported sections.
  const actualRole = ((user?.staffRole ?? FALLBACK_ROLE) as RoleId);
  const canSwitch = actualRole === 'super_admin';

  const [previewRole, setPreviewRole] = useState<RoleId | null>(null);
  useEffect(() => {
    if (canSwitch) setPreviewRole(readPreview());
    else setPreviewRole(null); // ignore any stale preview when not super_admin
  }, [canSwitch]);

  function setRole(next: RoleId) {
    if (!canSwitch) return; // no-op for non-super-admins
    setPreviewRole(next === actualRole ? null : next);
    if (typeof window !== 'undefined') {
      if (next === actualRole) window.localStorage.removeItem(STORAGE_KEY);
      else window.localStorage.setItem(STORAGE_KEY, next);
    }
  }

  return { role: previewRole ?? actualRole, setRole, canSwitch };
}
